<template>
  <section class="py-12 grid grid-cols-3 gap-8">
    <!-- Facets/Filters -->
    <CertificateHolderFacetGroup
      :filters="filters"
      :certificate-types="certificateTypes"
      :alpha-letters="alphaLetters"
      :show-all-types="showAllTypes"
      @update:filters="updateFilters"
      @toggle-types="showAllTypes = $event"
    />
    <!-- Results -->
    <div class="col-span-3 lg:col-span-2">
      <main>
        <section class="flex flex-col">
          <div class="mb-4 flex flex-row items-center justify-between">
            <span class="text-sm text-gray-700">{{ filteredHolders.length }} result{{ filteredHolders.length === 1 ? '' : 's' }}</span>
            <button
              v-if="hasActiveFilters"
              @click="resetFilters"
              class="text-xs text-gray-500 underline"
              type="button"
              aria-label="Clear all filters"
            >Clear all filters</button>
          </div>

          <CertificateHolderResult :holders="paginatedHolders" />
          <!-- Pagination -->
          <nav v-if="totalPages > 1" class="mt-8 flex flex-row gap-2" aria-label="Pagination">
            <button
              :disabled="page === 1"
              @click="page--"
              class="px-2 py-1 border rounded disabled:opacity-50"
              aria-label="Previous page"
              type="button"
            >&lt;</button>
            <span class="px-2 py-1">{{ page }} / {{ totalPages }}</span>
            <button
              :disabled="page === totalPages"
              @click="page++"
              class="px-2 py-1 border rounded disabled:opacity-50"
              aria-label="Next page"
              type="button"
            >&gt;</button>
          </nav>
        </section>
      </main>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import CertificateHolderFacetGroup from './CertificateHolderFacetGroup.vue'
import CertificateHolderResult from './CertificateHolderResult.vue'

interface Certificate {
  title: string
  slug: string | null
  url: string | null
  live: boolean
}

interface Holder {
  id: string | number
  first_name: string
  middle_name?: string | undefined
  last_name: string
  company?: string | undefined
  city?: string | undefined
  state?: string | undefined
  country?: string | undefined
  email?: string | undefined
  certificates: Certificate[]
}

const holders = ref<Holder[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const page = ref(1)
const pageSize = 20

const filters = ref({
  keyword: '',
  lastName: '',
  certificateTypes: [] as string[],
})

const showAllTypes = ref(false)

const fetchHolders = async () => {
  loading.value = true
  error.value = null
  try {
    const res = await fetch('/api/v2/certificateholders/')
    if (!res.ok) throw new Error('Failed to fetch certificate holders')
    const data = await res.json()
    holders.value = (data.items || []).map((item: any, idx: number) => ({
      id: item.holder.id ?? idx,
      first_name: item.holder.first_name ?? '',
      middle_name: item.holder.middle_name ?? '',
      last_name: item.holder.last_name ?? '',
      company: item.holder.company ?? '',
      city: item.holder.city ?? '',
      state: item.holder.state ?? '',
      country: item.holder.country ?? '',
      email: item.holder.email ?? '',
      certificates: (item.certificates || []).map((c: any) => ({
        title: c.title ?? '',
        slug: c.slug ?? null,
        url: c.url ?? null,
        live: c.live ?? false,
      })),
    }))
  } catch (e: any) {
    error.value = e.message
  } finally {
    loading.value = false
  }
}

onMounted(fetchHolders)

const alphaLetters = computed(() =>
  Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))
)

const certificateTypes = computed(() => {
  const set = new Set<string>()
  holders.value.forEach(holder =>
    holder.certificates.forEach(cert => {
      if (cert.title) set.add(cert.title)
    })
  )
  return Array.from(set).sort()
})

const filteredHolders = computed(() => {
  let result = holders.value
  if (filters.value.keyword.trim()) {
    const kw = filters.value.keyword.trim().toLowerCase()
    result = result.filter(holder =>
      [holder.first_name, holder.middle_name, holder.last_name, holder.company, holder.city, holder.state, holder.country, holder.email]
        .filter(Boolean)
        .some(field => field!.toLowerCase().includes(kw)) ||
      holder.certificates.some(cert => cert.title.toLowerCase().includes(kw))
    )
  }
  if (filters.value.lastName) {
    result = result.filter(holder =>
      holder.last_name && holder.last_name[0]?.toUpperCase() === filters.value.lastName
    )
  }
  if (filters.value.certificateTypes.length > 0) {
    result = result.filter(holder =>
      holder.certificates.some(cert =>
        filters.value.certificateTypes.includes(cert.title)
      )
    )
  }
  return result
})

const totalPages = computed(() =>
  Math.max(1, Math.ceil(filteredHolders.value.length / pageSize))
)
const paginatedHolders = computed(() =>
  filteredHolders.value.slice((page.value - 1) * pageSize, page.value * pageSize)
)

watch(filteredHolders, () => {
  page.value = 1
})

const hasActiveFilters = computed(() =>
  !!filters.value.keyword || !!filters.value.lastName || filters.value.certificateTypes.length > 0
)

function resetFilters() {
  filters.value.keyword = ''
  filters.value.lastName = ''
  filters.value.certificateTypes = []
}

function updateFilters(newFilters: typeof filters.value) {
  filters.value.keyword = newFilters.keyword
  filters.value.lastName = newFilters.lastName
  filters.value.certificateTypes = newFilters.certificateTypes
}
</script>
